import React from "react";
import { BrowserRouter, useRoutes, Navigate, Outlet, useParams, useLocation } from "react-router-dom";
import LanguageWrapper from "../components/LanguageWrapper/LanguageWrapper";
import Layout from "../components/Layout";
import ProfilePage from "../pages/profile";
import MyAccountPage from "../pages/myaccount";
import LoginPage from "../pages/login";
import APPage from "../pages/ap";
import { InjectAxiosInterceptors } from "../config/interceptors";
import TokenHandlerPage from "../pages/tokenHandler";
import ErrorPage from "../pages/error";
import AuthFailedPage from "../pages/authFailed";
import config from "../config/config";
import { useAppSelector } from "../redux/hooks";
import HATVPage from "../pages/hatv";
import ReportPage from "../pages/report";
import ConversationalAIPage from "../pages/conversationalAI";
import { useProfileParams } from "./common";
import AccountAssignmentPage from "../pages/accountassignment";

// Component to handle redirect from old URL format to new format with tabId
const ProfileRedirect: React.FC = () => {
  const params = useParams<{
    affiliateCode?: string;
    aboNumber?: string;
    type?: string;
  }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const userDetails: any = useAppSelector(
    (state: any) => state.userDetails.user
  );  
  const validTabIds = ["leaderInformation", "growthplan", "notesdocument", "businessperformance", "hatvqmonth", "hatvawards"];
  const pathParts = location.pathname.split("/").filter(Boolean);
  
  // Check if first param after "profile" is a valid tabId (old format doesn't have tabId)
  // If path is /profile/affiliateCode/aboNumber, pathParts[1] will be affiliateCode (not a tabId)
  // If path is /profile/tabId/affiliateCode/aboNumber, pathParts[1] will be a tabId
  const firstParam = pathParts[1];
  const isOldFormat = firstParam && !validTabIds.includes(firstParam);
  
  if (isOldFormat && params.affiliateCode && params.aboNumber) {
    let defaultTabId = searchParams.get("tab") || localStorage.getItem("selectedLink") || "businessperformance";

    // If trying to access businessperformance without permission, redirect to growthplan
    if (defaultTabId === "businessperformance" && !userDetails?.isAccessibleABOGrowthCompanion) {
      defaultTabId = "growthplan"; // change this if this become flag based
    }
    const redirectUrl = params.type
      ? `/profile/${defaultTabId}/${params.affiliateCode}/${params.aboNumber}/${params.type}`
      : `/profile/${defaultTabId}/${params.affiliateCode}/${params.aboNumber}`;
    return <Navigate to={redirectUrl} replace />;
  }
  
  // If it's not old format, redirect to myaccount (safety check)
  return <Navigate to="/myaccount" replace />;
};

/**
 * This is a function that defines the AppRoute component.
 *
 * @return {ReactNode} The routes for the AppRoute component.
 */
const AppRoute = () => {
  // const storedUserDetails = loadStateFromSessionStorage();
  const storedUserDetails = useProfileParams();
  const userDetails: any = useAppSelector(
    (state: any) => state.userDetails.user
  );

  let routes = useRoutes([
    {
      path: "/",
      element: <Navigate to={`/login`} replace />,
    },
    { path: "/token-handler", element: <TokenHandlerPage /> },
    { path: "/auth-failed", element: (
      <Layout isLoginPage>
        <LanguageWrapper>
          <AuthFailedPage />
        </LanguageWrapper>
      </Layout>
    ) },
    {
      path: "/error",
      element: (
        <Layout isLoginPage>
          <LanguageWrapper>
            <ErrorPage />
          </LanguageWrapper>
        </Layout>
      ),
    },
    {
      path: "/login",
      element: (
        <Layout isLoginPage>
          <LanguageWrapper>
            <LoginPage />
          </LanguageWrapper>
        </Layout>
      ),
    },
    {
      path: "/",
      element: (
        <Layout>
          <LanguageWrapper>
            <Outlet />
          </LanguageWrapper>
        </Layout>
      ),

      children: [
        { path: "myaccount", element: <MyAccountPage /> },
        {
          path: "profile",
          element: (() => {
            if (storedUserDetails &&
                storedUserDetails?.affiliateCode &&
                storedUserDetails?.aboNumber) {
              // Check if user has access to ABO Growth Companion
              const defaultTab = userDetails?.isAccessibleABOGrowthCompanion 
                ? "businessperformance" 
                : "growthplan";
              const basePath = `/profile/${defaultTab}/${storedUserDetails.affiliateCode}/${storedUserDetails.aboNumber}`;
              const url = storedUserDetails?.type
                ? `${basePath}/${storedUserDetails.type}`
                : basePath;
              return <Navigate to={url} replace />;
            }
            return <Navigate to={`/myaccount`} replace />;
          })(),
        },
        {
          path: "profile/:tabId/:affiliateCode/:aboNumber/:type?",
          element: <ProfilePage />,
        },
        // Handle old URL format (without tabId) - redirect to new format with default tab
        {
          path: "profile/:affiliateCode/:aboNumber/:type?",
          element: <ProfileRedirect />,
        },
        {
          path: "report",
          element: userDetails?.isAccessibleReportBuilderFeature ? (
            <ReportPage />
          ) : (
            <Navigate to={`/myaccount`} replace />
          ),
        },
        {
          path: "hatv",
          element:
            userDetails?.isAccessibleHATVFeature ? (
              <HATVPage />
            ) : (
              <Navigate to={`/myaccount`} replace />
            ),
        },
        {
          path: "ap",
          element:
            config?.isAPFeatureEnabled && userDetails?.isAccessibleAPFeature ? (
              <APPage />
            ) : (
              <Navigate to={`/myaccount`} replace />
            ),
        },
        {
          path: "amintelligence",
          element: userDetails?.isAccessibleConverseAIFeature ? (<ConversationalAIPage />) : (<Navigate to={`/myaccount`} replace />),
        },
        {
          path: "assignment",
          element:
            true ? (
              <AccountAssignmentPage />
            ) : (
              <Navigate to={`/myaccount`} replace />
            ),
        },
        {
          path: "assignment/:affiliateCode/:aboNumber/:role",
          element :
          true ? (
            <AccountAssignmentPage />
          ) : (
            <Navigate to={`/myaccount`} replace />
          ),
        }
        
      ],
    },
    // All path that are not defined will be redirected to the my accounts page
    {
      path: "*",
      element: <Navigate to={`/myaccount`} replace />,
    },
  ]);

  return routes;
};

function Router() {
  return (
    <BrowserRouter>
      <InjectAxiosInterceptors />
      <AppRoute />
    </BrowserRouter>
  );
}

export default Router;
